import { Link, Outlet, useLocation } from "react-router";
import {
  isRouteEnabled,
  paths,
  type RouteId,
} from "@shared-core/routing/routes";
import {
  useCanCurateCatalogue,
  useCanReadManufacturingSetup,
} from "../app/auth/hooks";

interface LiveCostingTab {
  route: RouteId;
  path: string;
  label: string;
  /** Mirrors this tab's route-level `ProtectedRoute` tier, so an engineer
   *  (manufacturing-setup-only) never sees a tab that would bounce them —
   *  Checks stayed `catalogueCuration` (admin/manager) when it moved here;
   *  the rest are `manufacturingSetupTier` (admin/manager/engineer).
   *  Combined with `isRouteEnabled` below, same as `SettingsLayout`'s
   *  `openPanels` — a tab must be both feature-enabled and role-permitted. */
  canOpen: boolean;
}

/** Segment-boundary path match, same rule as `SettingsLayout`'s: a tab
 *  matches its own path and any descendant, never a sibling with a longer
 *  static prefix. */
function matchesPath(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Settings › Catalogue › Live costing (product-owner decision, 12 Sep,
 * docs/features/cost-engine-productionisation/03_manufacturing_surfaces_design.md
 * "IA — Manufacturing nests under Settings › Catalogue as Live costing"):
 * Manufacturing exists only as a by-product of catalogue live pricing, so it
 * is folded in here, renamed, as an in-page tab strip rather than a settings
 * area of its own. Status, Checks, Routing policy, Manufacturing setup and
 * Check settings are five separate routes (each direct-load safe and
 * deep-linkable) sharing this one heading and tab row.
 */
export function SettingsLiveCostingLayout() {
  const location = useLocation();
  const canCurateCatalogue = useCanCurateCatalogue();
  const canReadManufacturingSetup = useCanReadManufacturingSetup();

  const tabs: LiveCostingTab[] = [
    {
      route: "settingsLiveCostingStatus",
      path: paths.settingsLiveCostingStatus(),
      label: "Status",
      canOpen: canReadManufacturingSetup,
    },
    {
      route: "settingsLiveCostingChecks",
      path: paths.settingsLiveCostingChecks(),
      label: "Checks",
      canOpen: canCurateCatalogue,
    },
    {
      route: "settingsLiveCostingRoutingPolicy",
      path: paths.settingsLiveCostingRoutingPolicy(),
      label: "Routing policy",
      canOpen: canReadManufacturingSetup,
    },
    {
      route: "settingsLiveCostingManufacturingSetup",
      path: paths.settingsLiveCostingManufacturingSetup(),
      label: "Manufacturing setup",
      canOpen: canReadManufacturingSetup,
    },
    {
      route: "settingsLiveCostingCheckSettings",
      path: paths.settingsLiveCostingCheckSettings(),
      label: "Check settings",
      canOpen: canReadManufacturingSetup,
    },
  ];
  const visibleTabs = tabs.filter(
    (tab) => isRouteEnabled(tab.route) && tab.canOpen,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Live costing</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Prices costed by One&rsquo;s own engine instead of a Tharstern
          estimate. The estate-wide status, the checks behind it, the routing
          rules it follows and the manufacturing setup it costs against.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border pb-2">
        {visibleTabs.map((tab) => {
          const active = matchesPath(location.pathname, tab.path);
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                active
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>

      <Outlet />
    </div>
  );
}
